import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, Users } from 'lucide-react';
import HistorySection from '../components/HistorySection';
import HomeStatsSection from '../components/HomeStatsSection';
import { BlocksRenderer } from '../components/BlockRenderer';
import { usePageBlocks } from '../components/PageBlocks';
import { useTranslation } from '../i18nContext';
import { buildLocalePath, isValidLocale } from '../i18n';
import { api } from '../lib/api';

export default function HomePage() {
  const { lang } = useParams();
  const t = useTranslation();
  const locale = lang && isValidLocale(lang) ? lang : 'es';

  // Default order/visibility used when API hasn't responded yet.
  const [sections, setSections] = useState([
    { key: 'hero', visible: true, sort_order: 10 },
    { key: 'stats', visible: true, sort_order: 15 },
    { key: 'history', visible: true, sort_order: 20 },
  ]);
  const { blocks, replacesDefault } = usePageBlocks('home');

  useEffect(() => {
    api.get('/home-sections')
      .then(({ data }) => { if (Array.isArray(data) && data.length) setSections(data); })
      .catch(() => {});
  }, []);

  const HeroSection = (
    <section key="hero" className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 bg-gradient-to-br from-cmic-800 via-cmic-700 to-cmic-600" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cmic-300 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cmic-400 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/20">
            <span className="w-2 h-2 bg-cmic-300 rounded-full animate-pulse" />
            <span className="text-sm text-cmic-100 font-medium">{t.hero.since}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t.hero.titleLine1}
            <span className="block text-cmic-200">{t.hero.titleLine2}</span>
          </h1>
          <p className="text-xl sm:text-2xl text-cmic-100 font-light leading-relaxed mb-4 max-w-2xl">{t.hero.tagline}</p>
          <p className="text-base text-cmic-200/80 leading-relaxed mb-10 max-w-2xl">{t.hero.description}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to={buildLocalePath(locale, 'directory')} data-testid="home-cta-directory" className="inline-flex items-center justify-center gap-2 bg-white text-cmic-700 px-8 py-4 rounded-lg font-semibold text-base hover:bg-cmic-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              <BookOpen size={20} />
              {t.hero.ctaDirectory}
            </Link>
            <Link to={buildLocalePath(locale, 'membership')} data-testid="home-cta-membership" className="inline-flex items-center justify-center gap-2 bg-cmic-500/30 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold text-base hover:bg-cmic-500/50 transition-all duration-200 hover:-translate-y-0.5">
              <Users size={20} />
              {t.hero.ctaMembership}
            </Link>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white" />
        </svg>
      </div>
    </section>
  );

  const SECTION_RENDERERS = {
    hero: HeroSection,
    stats: <HomeStatsSection key="stats" />,
    history: <HistorySection key="history" />,
  };

  return (
    <>
      {!replacesDefault && sections
        .filter((s) => s.visible !== false && SECTION_RENDERERS[s.key])
        .map((s) => SECTION_RENDERERS[s.key])}
      <BlocksRenderer blocks={blocks} locale={locale} />
    </>
  );
}
